import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { ArtistsBrief } from "../../../api/artists-briefs";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";

const MAX_LINES = 3;

// Keep the last two words on the same line so the expanded text never ends
// with a single-word row (matches the storefront).
const preventWidow = (text: string) => {
  const trimmed = text.trimEnd();
  const lastSpace = trimmed.lastIndexOf(" ");
  return lastSpace === -1
    ? trimmed
    : trimmed.slice(0, lastSpace) + " " + trimmed.slice(lastSpace + 1);
};

const PreviewBriefCard = (props: { brief: ArtistsBrief }) => {
  const { brief } = props;
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  const measure = useCallback(() => {
    const element = descriptionRef.current;
    if (!element || expanded) return;
    const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
    const lineCount = lineHeight
      ? Math.round(element.scrollHeight / lineHeight)
      : 0;
    setIsTruncated(
      lineHeight
        ? lineCount > MAX_LINES
        : element.scrollHeight > element.clientHeight + 1,
    );
  }, [expanded]);

  useLayoutEffect(() => {
    measure();
  }, [measure, brief.description]);

  useEffect(() => {
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure);
    }
    const element = descriptionRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    // Re-measure once the dialog lays the card out at its real width (the
    // initial layout-effect runs before the open transition settles).
    const observer = new ResizeObserver(() => measure());
    observer.observe(element);
    return () => observer.disconnect();
  }, [measure]);

  return (
    <DSBox
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: 350,
        justifySelf: "center",
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
      }}
    >
      {brief.imageUrl && (
        <img
          src={brief.imageUrl}
          alt={brief.title}
          style={{ width: "100%", objectFit: "contain", display: "block" }}
        />
      )}
      <DSBox sx={{ p: "20px", textAlign: "center" }}>
        <DSTypography
          variant="h5"
          sx={{ fontWeight: 700, fontSize: 20, lineHeight: 1.1, m: "0 0 21px" }}
        >
          {brief.title}
        </DSTypography>
        {brief.description && (
          <>
            <DSBox
              ref={descriptionRef}
              sx={{
                m: "0 0 8px",
                color: "#000000",
                fontSize: 16,
                fontWeight: 500,
                lineHeight: "150%",
                ...(expanded
                  ? { textWrap: "pretty" }
                  : {
                      display: "-webkit-box",
                      WebkitLineClamp: MAX_LINES,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }),
              }}
            >
              {expanded ? preventWidow(brief.description) : brief.description}
            </DSBox>
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              style={{
                display: "block",
                margin: "0 auto 32px",
                padding: 0,
                border: "none",
                background: "none",
                cursor: "pointer",
                color: "rgba(12, 12, 12, 0.5)",
                fontSize: 14,
                fontWeight: 400,
                textDecoration: "underline",
                visibility: isTruncated || expanded ? "visible" : "hidden",
              }}
            >
              {expanded ? "See less" : "Read more"}
            </button>
          </>
        )}
        {brief.labels && brief.labels.length > 0 && (
          <DSBox
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            {brief.labels.map((label) => (
              <DSBox
                key={label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "31px",
                  px: "10px",
                  borderRadius: "51px",
                  backgroundColor: "#ffd848",
                  color: "#000000",
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {label}
              </DSBox>
            ))}
          </DSBox>
        )}
      </DSBox>
    </DSBox>
  );
};

const PreviewBriefsDialog = (props: {
  briefs: ArtistsBrief[];
  onClose: () => void;
}) => {
  return (
    <>
      <DSDialogTitle>Preview — how the cards appear on the site</DSDialogTitle>
      <DSDialogContent sx={{ backgroundColor: "#f5f2f9" }}>
        {props.briefs.length === 0 ? (
          <DSTypography sx={{ textAlign: "center", py: 4 }}>
            No cards to preview.
          </DSTypography>
        ) : (
          <DSBox
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gap: "32px",
              py: 3,
              alignItems: "start",
            }}
          >
            {props.briefs.map((brief) => (
              <PreviewBriefCard key={brief.id} brief={brief} />
            ))}
          </DSBox>
        )}
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={props.onClose}>Close</DSSecondaryButton>
      </DSDialogActions>
    </>
  );
};

export default PreviewBriefsDialog;
